/**
 * The prompt layer's authoring rules — D235 (`docs/design/actors.md` §4, §7;
 * Mike's ruling 6 of 2026-10-04), and the gift table beside it.
 *
 * @remarks
 * A prompt is a question an actor asks (content-schema `prompts.ts`), and
 * every rule here is the reference walk or the D31/D47 dead-content law
 * applied to it:
 *
 * - **Prompt ids are unique across the pack** — the asked ledger
 *   (`narrative.askedPromptIds`) and the `ask_prompt` effect name a prompt by
 *   id alone, so two prompts under one id would ask and spend each other.
 * - **A `leadsTo` names a prompt of the SAME actor.** The back-and-forth is
 *   one body's; a question that leads to another body's question would
 *   change speakers mid-sentence, and the engine asks the next prompt in the
 *   asker's own voice.
 * - **A `leadsTo` cycle is refused unless every prompt in it is
 *   `repeatable`** — a non-repeatable prompt in a cycle is asked once and
 *   then the cycle breaks there, so the loop never loops; and a cycle of
 *   repeatable prompts is a conversation that can go round, which an author
 *   may want (a guard who asks the same three things).
 * - **A prompt nothing opens is dead** — no `opensWith`, no `leadsTo`, no
 *   `ask_prompt` anywhere names it; every line it has is unreachable.
 * - **A prompt's `ask_prompt` is aimed at its own actor** — the effect names
 *   an actor and a prompt, and the prompt must be that actor's.
 * - **Every option's `when`, lines and effects, and the prompt's unanswered
 *   lines, walk like a topic's.** An option's `effects` is an effect home
 *   (`effect-homes.ts`) and is walked with every other.
 * - **A gift arm names something the player can carry** — a portable thing,
 *   or one they start with; a `giveInteractions` arm for a thing that can
 *   never be in the hands could never run. Its conditions, lines and
 *   effects walk like a `use` interaction's.
 *
 * @packageDocumentation
 */

import { type ActorDefinition, type ContentPackDefinition, everyEffectOf, type PromptDefinition } from '@orpheus/content-schema';

import { brokenReference, collectDuplicates } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, checkNarrativeLines, type DeclaredIdSets, declaredIdSetsOf } from './reference-walkers.js';

/**
 * Validate every prompt and every gift arm in the pack.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validatePromptAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const everInTheHands = new Set<string>([
		...contentPack.items.flatMap((item) => (item.isPortable ? [item.itemId as string] : [])),
		...contentPack.manifest.initialInventory.map((itemId) => itemId as string),
	]);
	const asks = everyEffectOf(contentPack).flatMap((effect) => (effect.type === 'ask_prompt' ? [effect] : []));

	collectDuplicates(
		contentPack.actors.flatMap((actor, actorIndex) =>
			(actor.prompts ?? []).map((prompt, promptIndex) => ({
				declarationPath: `actors[${String(actorIndex)}].prompts[${String(promptIndex)}].promptId`,
				declaredId: prompt.promptId,
			})),
		),
		'prompt',
		failures,
	);

	contentPack.actors.forEach((actor, actorIndex) => {
		const actorPath = `actors[${String(actorIndex)}]`;
		const ownPromptIds = new Set((actor.prompts ?? []).map((prompt) => prompt.promptId as string));
		const openedByLeadsTo = new Set((actor.prompts ?? []).flatMap((prompt) => prompt.options.flatMap((option) => (option.leadsTo === undefined ? [] : [option.leadsTo as string]))));
		const openedByEffect = new Set(asks.filter((ask) => ask.actorId === actor.actorId).map((ask) => ask.promptId as string));

		(actor.prompts ?? []).forEach((prompt, promptIndex) => {
			const promptPath = `${actorPath}.prompts[${String(promptIndex)}]`;

			if (actor.opensWith !== prompt.promptId && !openedByLeadsTo.has(prompt.promptId) && !openedByEffect.has(prompt.promptId)) {
				failures.push(
					brokenReference(
						`${promptPath}.promptId`,
						prompt.promptId,
						'nothing opens this prompt — no opensWith, no leadsTo and no ask_prompt names it — so it is never asked and every line it has is unreachable',
					),
				);
			}

			checkPromptProse(prompt, promptPath, ownPromptIds, declared, failures);
		});

		checkLeadsToCycles(actor, actorPath, failures);

		actor.giveInteractions.forEach((interaction, interactionIndex) => {
			const interactionPath = `${actorPath}.giveInteractions[${String(interactionIndex)}]`;

			if (!declared.itemIds.has(interaction.itemId)) {
				failures.push(brokenReference(`${interactionPath}.itemId`, interaction.itemId, 'the gift arm names an undeclared item'));
			} else if (!everInTheHands.has(interaction.itemId)) {
				failures.push(
					brokenReference(
						`${interactionPath}.itemId`,
						interaction.itemId,
						'the gift arm names a thing the player can never carry — not portable, not in the initial inventory — so it could never be handed over and the arm could never run',
					),
				);
			}

			// D244: a thing shown is never kept, so a `show` arm that keeps it says something no turn can do.
			if (interaction.act === 'show' && interaction.consumesItem) {
				failures.push(brokenReference(`${interactionPath}.consumesItem`, interaction.itemId, 'a `show` arm keeps nothing — a thing held up stays in the hands; make the arm `give`, or drop consumesItem'));
			}

			interaction.conditions.forEach((condition, conditionIndex) => {
				checkCondition(condition, `${interactionPath}.conditions[${String(conditionIndex)}]`, declared, failures);
			});
			checkNarrativeLines(interaction.narratives, `${interactionPath}.narratives`, declared, failures);
		});

		checkNarrativeLines(actor.refusesGiftsNarratives, `${actorPath}.refusesGiftsNarratives`, declared, failures);
	});

	// Every ask, wherever it lives, aims a prompt at the actor that declares it.
	const actorByPromptId = new Map(contentPack.actors.flatMap((actor) => (actor.prompts ?? []).map((prompt) => [prompt.promptId as string, actor.actorId as string])));

	asks.forEach((ask) => {
		const owner = actorByPromptId.get(ask.promptId);

		if (owner !== undefined && owner !== ask.actorId) {
			failures.push(
				brokenReference(
					`ask_prompt(${ask.actorId}, ${ask.promptId})`,
					ask.promptId,
					`the prompt belongs to "${owner}", not to the actor the effect has asking it — a question is asked in its own actor's voice`,
				),
			);
		}
	});

	return failures;
};

/**
 * Every id a prompt's prose names must exist, and every `leadsTo` must name
 * a prompt of the same actor.
 *
 * @param prompt - The prompt
 * @param promptPath - Its path, for the failures
 * @param ownPromptIds - The ids of the asking actor's prompts
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkPromptProse = (
	prompt: PromptDefinition,
	promptPath: string,
	ownPromptIds: ReadonlySet<string>,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	prompt.options.forEach((option, optionIndex) => {
		const optionPath = `${promptPath}.options[${String(optionIndex)}]`;

		if (option.when !== undefined) {
			checkCondition(option.when, `${optionPath}.when`, declared, failures);
		}

		checkNarrativeLines(option.narratives.length === 0 ? undefined : option.narratives, `${optionPath}.narratives`, declared, failures);

		if (option.leadsTo !== undefined && !ownPromptIds.has(option.leadsTo)) {
			failures.push(
				brokenReference(
					`${optionPath}.leadsTo`,
					option.leadsTo,
					declared.promptIds.has(option.leadsTo)
						? 'leadsTo names another actor\'s prompt — the next question is asked in the same actor\'s voice, so it must be one of its own'
						: 'leadsTo names a prompt nobody declares',
				),
			);
		}
	});

	checkNarrativeLines(prompt.unansweredNarratives, `${promptPath}.unansweredNarratives`, declared, failures);
};

/**
 * Refuse a `leadsTo` cycle that holds a prompt which is not `repeatable`: the
 * loop breaks at that prompt the second time round, so what was authored as
 * a loop is a chain pretending — or, if every prompt is repeatable, a
 * conversation that may go round, which is allowed.
 *
 * @param actor - The actor whose prompts are walked
 * @param actorPath - Its path, for the failures
 * @param failures - Accumulator
 */
const checkLeadsToCycles = (actor: ActorDefinition, actorPath: string, failures: ReferenceBrokenFailure[]): void => {
	const prompts = actor.prompts ?? [];
	const byId = new Map(prompts.map((prompt) => [prompt.promptId as string, prompt]));
	const reported = new Set<string>();

	prompts.forEach((start, startIndex) => {
		// Walk every path of `leadsTo` edges from this prompt; a path that comes
		// back to it is a cycle. Paths are short (a conversation), so the walk
		// is a plain depth-first search with the path in hand.
		const stack: (readonly string[])[] = [[start.promptId]];

		while (stack.length > 0) {
			const path = stack.pop() as readonly string[];
			// Every id on a path was found in `byId` before it was pushed (the start is the walk's own prompt).
			const last = byId.get(path.at(-1) as string) as PromptDefinition;

			for (const option of last.options) {
				const next = option.leadsTo as string | undefined;

				if (next === undefined || !byId.has(next)) {
					continue;
				}

				if (next === start.promptId) {
					const cycleKey = [...path].sort().join('>');

					if (!reported.has(cycleKey) && path.some((promptId) => byId.get(promptId)?.repeatable !== true)) {
						reported.add(cycleKey);
						failures.push(
							brokenReference(
								`${actorPath}.prompts[${String(startIndex)}].promptId`,
								start.promptId,
								`leadsTo runs in a cycle (${[...path, start.promptId].join(' → ')}) through a prompt that is not repeatable — the loop would break there the second time round; mark every prompt in it repeatable, or end the conversation`,
							),
						);
					}
				} else if (!path.includes(next)) {
					stack.push([...path, next]);
				}
			}
		}
	});
};
